import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Util from "@/js/common/Util";
import React from "react";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Axios from "@/js/common/Axios";
import { exportToXlsx } from "@/js/common/ExcelExport";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList } from "react-tabs";

const SysBgtSbjctMng = () => {
  // =========================
  // 탭: 수입(01) / 지출(02)
  // =========================
  const [tabIndex, setTabIndex] = React.useState<number>(0);
  const [bgtSbjctSeCd, setBgtSbjctSeCd] = React.useState<"01" | "02">("01"); 

  const tabTitle = bgtSbjctSeCd === "01" ? "수입" : "지출";

  // =========================
  // 검색 조건
  // =========================
  const [spcAcntgSeCd, setSpcAcntgSeCd] = React.useState<string>(""); // 시스템코드(C1~C5)
  const [bgtSbjct1stCd, setBgtSbjct1stCd] = React.useState<string>("");
  const [bgtSbjct2stCd, setBgtSbjct2stCd] = React.useState<string>("");
  const [bgtSbjct3stCd, setBgtSbjct3stCd] = React.useState<string>("");
  const [callTime, setCallTime] = React.useState<Date>(new Date());

  // =========================
  // 목록
  // =========================
  const [dataArr, setDataArr] = React.useState<any[]>([]);

  const fldInfs: fldInfsProps[] = [
    { type: "text", fldNm: "bgtSbjctSeNm", width: 120, headAlign: "center", dataAlign: "center", label: "과목유형" },
    { type: "text", fldNm: "bgtSbjct1stNm", width: 160, headAlign: "center", dataAlign: "left", label: "관" },
    { type: "text", fldNm: "bgtSbjct2stNm", width: 220, headAlign: "center", dataAlign: "left", label: "항" },
    { type: "text", fldNm: "bgtSbjct3stNm", width: 360, headAlign: "center", dataAlign: "left", label: "목" },
  ];

  React.useEffect(() => {
    setBgtSbjct1stCd("");
    setBgtSbjct2stCd("");
    setBgtSbjct3stCd("");
    setCallTime(new Date());
  }, [bgtSbjctSeCd, spcAcntgSeCd]);

  // =========================
  // 조회 (sbjctSeCd만 탭에 따라 다름)
  // =========================
  React.useEffect(() => {
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/sys/spcbgt/select-spc-bgt-sbjct-list.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        bgtSbjct1stCd,
        bgtSbjct2stCd,
        bgtSbjct3stCd,
        spcAcntgSeCd,
        bgtSbjctSeCd,
      },
      onSuccessFn: (res: any) => {
        setDataArr(res?.result?.list || []);
        AppContext.hideSpinner();
      },
    });
  }, [callTime, bgtSbjct1stCd, bgtSbjct2stCd, bgtSbjct3stCd, spcAcntgSeCd, bgtSbjctSeCd]);

  // =========================
  // 엑셀 다운로드
  // =========================
  const downloadExcel = () => {
    if (!dataArr || dataArr.length === 0) {
      AppContext.openAlert({
        title: "데이터 확인",
        msg: "엑셀로 다운로드할 데이터가 없습니다.",
        icon: "warning",
      });
      return;
    }
    exportToXlsx({
      fileName: `특별회계기금 ${tabTitle} 예산 목록`,
      columns: fldInfs,
      rows: dataArr,
    });
  };

  // =========================
  // 관/항/목 코드 (특별회계기금 전용 코드로 변경)
  // - 관: spcAcntgSeCd + sbjctSeCd(수입/지출) 필요
  // - 항/목: spcAcntgSeCd + (관/항) 필요
  // =========================

  return (
    <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
      {/* 타이틀 */}
      <div className="mt5">
        <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
          특별회계기금 예산과목 분류 조회 ({tabTitle})
        </span>
        <div className="bdt3 bdc007bff pb10"></div>
      </div>

      {/* 수입/지출 탭*/}
      <StyledTabs selectedIndex={tabIndex} className="tabFit">
        <TabList>
          <Tab
            onClick={() => {
              setTabIndex(0);
              setBgtSbjctSeCd("01");}}
          >수입</Tab>
          <Tab
            onClick={() => {
              setTabIndex(1);
              setBgtSbjctSeCd("02");}}
          >지출</Tab>
        </TabList>
      </StyledTabs>

      {/* 검색 영역 */}
      <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
        <div className="dpFlx aiC gap14 jcSb">
          <div className="dpFlx aiC gap14">
            {/* 예산 구분(시스템코드 C1~C5) */}
            <div className="dpFlx aiC">
              <span className="w70 fm-c fs15 fc333333 ls1 taL">예산 구분</span>
              <SelectBox
                value={spcAcntgSeCd}
                width={150}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#fff"
                codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("SPC_ACNTG_SE_CD"))}
                placeholder="전체"
                paddingRight={8}
                onChangeFunc={(v: string) => setSpcAcntgSeCd(v)}
              />
            </div>

            {/* 관 */}
            <div className="dpFlx aiC">
              <span className="w40 fm-c fs15 fc333333 ls1 taL">관</span>
              <SelectBox
                value={bgtSbjct1stCd}
                width={120}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#fff"
                codeArray={[{ code: "", name: "전체" }].concat(Util.getSpcBgt1CodeArray(spcAcntgSeCd, bgtSbjctSeCd))} 
                placeholder="전체"
                paddingRight={8}
                onChangeFunc={(v: string) => {
                  setBgtSbjct1stCd(v);
                  setBgtSbjct2stCd("");
                  setBgtSbjct3stCd("");
                }}
              />
            </div>

            {/* 항 */}
            <div className="dpFlx aiC">
              <span className="w40 fm-c fs15 fc333333 ls1 taL">항</span>
              <SelectBox
                value={bgtSbjct2stCd}
                width={150}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#fff"
                codeArray={[{ code: "", name: "전체" }].concat(Util.getSpcBgt2CodeArray(spcAcntgSeCd, bgtSbjct1stCd))} 
                placeholder="전체"
                paddingRight={8}
                onChangeFunc={(v: string) => {
                  setBgtSbjct2stCd(v);
                  setBgtSbjct3stCd("");
                }}
              />
            </div>

            {/* 목 */}
            <div className="dpFlx aiC">
              <span className="w40 fm-c fs15 fc333333 ls1 taL">목</span>
              <SelectBox
                value={bgtSbjct3stCd}
                width={220}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#fff"
                codeArray={[{ code: "", name: "전체" }].concat(Util.getSpcBgt3CodeArray(spcAcntgSeCd, bgtSbjct2stCd))} 
                placeholder="전체"
                paddingRight={8}
                onChangeFunc={(v: string) => setBgtSbjct3stCd(v)}
              />
            </div>
          </div>

          {/* 버튼 */}
          <div className="dpFlx aiC gap12">
            <Button
              width={75}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              variant="styInitBtn"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName="초기화"
              onClickFunc={ () => {
                setSpcAcntgSeCd("");
                setBgtSbjct1stCd("");
                setBgtSbjct2stCd(""); 
                setBgtSbjct3stCd("");
              }}
            />
          </div>
        </div>
      </div>

      {/* 목록 타이틀 */}
      <div className="dpFlx aiC jcSb mb10 h44">
        <div className="fm-b fs16 fc007bff ls1 fw700">
          <span>특별회계기금 {tabTitle} 예산 목록</span>
        </div>
        <Button
          width={150}
          height={40}
          fontFamily="fm-b"
          fontSize={16}
          letterSpacing={-1}
          variant="styExcelBtn"
          borderRadius={6}
          buttonClass="dpFlx aiC jcC"
          buttonName="Excel 다운로드"
          onClickFunc={downloadExcel}
        />
      </div>

      {/* 그리드 */}
      <GridTable
        tableInfo={{
          tableHeadVsb: true,
          styleType: AdminGridStyle,
          fldInfs,
          ordFld: { name: "bgtSbjctSeNm", direction: "asc" },
          dataArr: Util.filterData(dataArr, fldInfs),
          maxRowNums: [15, 30, 50],
          theadTrHeight: 40,
          tbodyTrHeight: 35,
          paginateClass: "pt10",
          paginateStyle: {
            buttonOnColor: AppContext.boardThemeColor,
            buttonWidth: 22,
            buttonHeight: 24,
            buttonMarginLeft: 1,
            buttonMarginRight: 1,
            fontSize: 13,
            color: "#666666",
            letterSpacing: -0.8,
            fontFamily: "Pretendard-Regular",
            borderRadius: 3,
          },
          footerVsb: true,
        }}
      />
    </div>
  );
};

export default SysBgtSbjctMng;
